import React, { useEffect, useState } from "react";
import {
  fetchAdminJson,
  fetchAllPages,
  downloadPincodeSample,
  downloadAgentAssignmentSample,
  HIERARCHY_CITY_REGION,
  normalizeCityForHierarchy,
} from "../settingsHelpers";

// ── ClustersTab ────────────────────────────────────────────────────────────────

function ClusterModal({ modal, allCities, onClose, onSaved }) {
  const isEdit = modal.mode === "edit";
  const cluster = modal.cluster || null;

  const [name, setName] = useState(cluster?.name || "");
  const [cityName, setCityName] = useState(cluster?.city_name || "");
  const [pincodes, setPincodes] = useState(cluster?.pincodes || []);
  const [newPin, setNewPin] = useState("");
  const [newArea, setNewArea] = useState("");
  const [saving, setSaving] = useState(false);
  const [addingPin, setAddingPin] = useState(false);
  const [error, setError] = useState("");

  const handleSaveCluster = async () => {
    if (!name.trim() || !cityName) return;
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await fetchAdminJson(`/api/crm/clusters/${cluster.id}/`, {
          method: "PATCH",
          body: JSON.stringify({ name: name.trim(), city_name: cityName }),
        });
      } else {
        await fetchAdminJson("/api/crm/clusters/", {
          method: "POST",
          body: JSON.stringify({ name: name.trim(), city_name: cityName }),
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPincode = async () => {
    const pin = newPin.trim();
    if (!pin || pin.length < 4) return;
    setAddingPin(true);
    setError("");
    try {
      const result = await fetchAdminJson("/api/crm/pincode-mappings/", {
        method: "POST",
        body: JSON.stringify({
          pincode: pin,
          city_name: cityName || cluster?.city_name,
          area_name: newArea.trim(),
          cluster_name: name.trim() || cluster?.name,
        }),
      });
      setPincodes(prev => [...prev, { id: result.id, pincode: result.pincode, area_name: result.area_name }]);
      setNewPin("");
      setNewArea("");
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingPin(false);
    }
  };

  const handleRemovePincode = async (pm) => {
    setError("");
    try {
      await fetchAdminJson(`/api/crm/pincode-mappings/${pm.id}/`, { method: "DELETE" });
      setPincodes(prev => prev.filter(p => p.id !== pm.id));
      onSaved();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel"
        style={{ maxWidth: 540, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div>
            <p className="modal-kicker">{isEdit ? "Edit Cluster" : "New Cluster"}</p>
            <h2>{isEdit ? cluster.name : "Add Cluster"}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
          {error ? <div className="message-banner error" style={{ marginBottom: "1rem" }}>{error}</div> : null}

          {/* Cluster identity */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
            <label className="field-stack">
              <span>Cluster Name</span>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. South Delhi"
              />
            </label>
            <label className="field-stack">
              <span>City</span>
              <select
                value={cityName}
                onChange={e => setCityName(e.target.value)}
                disabled={isEdit}
              >
                <option value="">Select city</option>
                {allCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          {/* Pincodes section — only shown in edit mode */}
          {isEdit && (
            <>
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1.25rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)" }}>
                    Pincodes
                    <span style={{ background: "var(--surface-sunken)", borderRadius: "10px", color: "var(--text-tertiary)", fontSize: "11px", fontWeight: 500, marginLeft: 6, padding: "1px 7px" }}>
                      {pincodes.length}
                    </span>
                  </span>
                </div>

                {/* Existing pincodes */}
                {pincodes.length > 0 ? (
                  <div style={{ border: "1px solid var(--border)", borderRadius: "8px", marginBottom: "0.75rem", overflow: "hidden" }}>
                    {pincodes.map((p, idx) => (
                      <div
                        key={p.id}
                        style={{
                          alignItems: "center",
                          borderBottom: idx < pincodes.length - 1 ? "1px solid var(--border)" : "none",
                          display: "flex",
                          gap: "0.5rem",
                          padding: "0.45rem 0.75rem",
                        }}
                      >
                        <span style={{ fontFamily: "monospace", fontSize: "13px", fontWeight: 600, minWidth: 64 }}>{p.pincode}</span>
                        <span style={{ color: "var(--text-tertiary)", flex: 1, fontSize: "12px" }}>{p.area_name || "—"}</span>
                        <button
                          type="button"
                          onClick={() => handleRemovePincode(p)}
                          style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: "14px", lineHeight: 1, padding: "2px 4px", opacity: 0.7 }}
                          title="Remove pincode"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "var(--text-tertiary)", fontSize: "12px", marginBottom: "0.75rem", padding: "0.5rem 0" }}>
                    No pincodes yet.
                  </div>
                )}

                {/* Add pincode row */}
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr auto", gap: "0.5rem", alignItems: "end" }}>
                  <label className="field-stack" style={{ margin: 0 }}>
                    <span>Pincode</span>
                    <input
                      value={newPin}
                      onChange={e => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="e.g. 110001"
                      maxLength={6}
                      onKeyDown={e => e.key === "Enter" && handleAddPincode()}
                    />
                  </label>
                  <label className="field-stack" style={{ margin: 0 }}>
                    <span>Location (area)</span>
                    <input
                      value={newArea}
                      onChange={e => setNewArea(e.target.value)}
                      placeholder="e.g. Connaught Place"
                      onKeyDown={e => e.key === "Enter" && handleAddPincode()}
                    />
                  </label>
                  <button
                    type="button"
                    className="primary-button"
                    style={{ fontSize: "12px", padding: "6px 14px", whiteSpace: "nowrap" }}
                    disabled={addingPin || newPin.length < 4}
                    onClick={handleAddPincode}
                  >
                    {addingPin ? "Adding..." : "Add"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-actions" style={{ flexShrink: 0, borderTop: "1px solid var(--border)", padding: "1rem 1.25rem" }}>
          <button type="button" className="secondary-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="primary-button"
            disabled={saving || !name.trim() || !cityName}
            onClick={handleSaveCluster}
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Cluster"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClustersTab() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCity, setFilterCity] = useState("");
  const [modal, setModal] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [uploadingPincodes, setUploadingPincodes] = useState(false);
  const [uploadNotice, setUploadNotice] = useState("");
  const [pincodeUploadResult, setPincodeUploadResult] = useState(null);
  const pincodeFileRef = React.useRef(null);

  const loadClusters = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminJson("/api/crm/clusters/");
      setClusters(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClusters(); }, []);

  const handleDelete = async (cl) => {
    if (!window.confirm(`Delete cluster "${cl.name}" (${cl.city_name})?\nPincodes in this cluster will be unlinked but not deleted.`)) return;
    try {
      await fetchAdminJson(`/api/crm/clusters/${cl.id}/`, { method: "DELETE" });
      setNotice("Cluster deleted.");
      setTimeout(() => setNotice(""), 2500);
      await loadClusters();
    } catch (e) {
      setError(e.message);
    }
  };

  const handlePincodeUpload = async (file) => {
    if (!file) return;
    setUploadingPincodes(true);
    setUploadNotice("");
    setPincodeUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await fetchAdminJson("/api/crm/pincode-mappings/bulk-upload/", { method: "POST", body: fd });
      setPincodeUploadResult(result);
      setUploadNotice(
        `${result.created ?? 0} pincode row${result.created === 1 ? "" : "s"} created, ${result.updated ?? 0} updated, ${result.assigned ?? 0} assignment row${result.assigned === 1 ? "" : "s"} processed`,
      );
      await loadClusters();
    } catch (e) {
      setUploadNotice(`Upload failed: ${e.message}`);
    } finally {
      setUploadingPincodes(false);
      if (pincodeFileRef.current) pincodeFileRef.current.value = "";
    }
  };

  const displayed = filterCity
    ? clusters.filter(c => c.city_name.toLowerCase() === filterCity.toLowerCase())
    : clusters;

  const allCities = Object.keys(HIERARCHY_CITY_REGION).filter(c => c !== "Others");

  return (
    <div style={{ display: "grid", gap: "1.25rem" }}>
      {notice ? <div className="message-banner success">{notice}</div> : null}
      {error ? <div className="message-banner error">{error}</div> : null}

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <select
          value={filterCity}
          onChange={e => setFilterCity(e.target.value)}
          style={{ fontSize: "13px", padding: "5px 10px", border: "1px solid var(--border)", borderRadius: "6px", background: "var(--surface)" }}
        >
          <option value="">All cities</option>
          {allCities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label style={{ cursor: "pointer" }}>
          <input
            ref={pincodeFileRef}
            type="file"
            accept=".csv,.xlsx"
            style={{ display: "none" }}
            onChange={e => handlePincodeUpload(e.target.files[0])}
          />
          <span className="secondary-ghost" style={{ fontSize: "12px", padding: "5px 12px", border: "1px solid var(--border)", borderRadius: "6px", cursor: "pointer", display: "inline-block" }}>
            {uploadingPincodes ? "Uploading..." : "Upload Pincodes"}
          </span>
        </label>
        <button
          type="button"
          className="secondary-ghost"
          style={{ fontSize: "12px", padding: "5px 12px" }}
          onClick={downloadPincodeSample}
        >
          Sample File
        </button>
        {uploadNotice
          ? <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>{uploadNotice}</span>
          : <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>Columns: Pin · Location · Cluster · City</span>
        }

        <button
          type="button"
          className="primary-button"
          style={{ marginLeft: "auto", fontSize: "13px", padding: "5px 14px" }}
          onClick={() => setModal({ mode: "create" })}
        >
          + Add Cluster
        </button>
      </div>

      {pincodeUploadResult?.errors?.length ? (
        <div className="message-banner error" style={{ display: "grid", gap: 6 }}>
          {pincodeUploadResult.errors.slice(0, 8).map((item, index) => (
            <span key={index}>{item.error}</span>
          ))}
          {pincodeUploadResult.errors.length > 8 ? <span>...and {pincodeUploadResult.errors.length - 8} more.</span> : null}
        </div>
      ) : null}

      {/* Cluster table */}
      {loading ? (
        <div style={{ color: "var(--text-tertiary)", fontSize: "13px" }}>Loading clusters...</div>
      ) : displayed.length === 0 ? (
        <div style={{ color: "var(--text-tertiary)", fontSize: "13px" }}>No clusters found. Upload a pincode CSV or add manually.</div>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--surface-sunken)", borderBottom: "1px solid var(--border)" }}>
                {["Cluster", "City", "Pincodes", ""].map(h => (
                  <th key={h} style={{ color: "var(--text-tertiary)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", padding: "0.5rem 1rem", textAlign: h === "" ? "right" : "left", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((cl, idx) => (
                <tr key={cl.id} style={{ borderBottom: idx < displayed.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <td style={{ padding: "0.65rem 1rem", fontWeight: 600 }}>{cl.name}</td>
                  <td style={{ padding: "0.65rem 1rem", color: "var(--text-secondary)" }}>{cl.city_name}</td>
                  <td style={{ padding: "0.65rem 1rem" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", alignItems: "center" }}>
                      {(cl.pincodes || []).slice(0, 8).map(p => (
                        <span key={p.pincode} title={p.area_name} style={{ background: "var(--surface-sunken)", border: "1px solid var(--border)", borderRadius: "3px", color: "var(--text-secondary)", fontFamily: "monospace", fontSize: "11px", padding: "1px 5px" }}>
                          {p.pincode}
                        </span>
                      ))}
                      {(cl.pincodes || []).length > 8 && (
                        <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>+{cl.pincodes.length - 8} more</span>
                      )}
                      {(cl.pincodes || []).length === 0 && (
                        <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>No pincodes</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "0.65rem 1rem", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                      <button type="button" className="secondary-ghost" style={{ fontSize: "11px", padding: "3px 10px" }} onClick={() => setModal({ mode: "edit", cluster: cl })}>
                        Edit
                      </button>
                      <button type="button" className="secondary-ghost" style={{ fontSize: "11px", padding: "3px 10px", color: "var(--red)" }} onClick={() => handleDelete(cl)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ClusterModal
          modal={modal}
          allCities={allCities}
          onClose={() => setModal(null)}
          onSaved={loadClusters}
        />
      )}
    </div>
  );
}

// ── CityAssignmentPanel ────────────────────────────────────────────────────────

function CityAssignmentPanel({ users, roleLabel, onSaved, refreshKey = 0 }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [cityNames, setCityNames] = useState([]);
  // { cityName: Set<clusterName> } — empty Set = all clusters (no restriction)
  const [clusterScopesByCity, setClusterScopesByCity] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  // { cityName: [{name, pincode_count, pincodes}] }
  const [clustersByCity, setClustersByCity] = useState({});

  const loadClusters = () => {
    fetchAdminJson("/api/crm/clusters/")
      .then((data) => {
        const map = {};
        (Array.isArray(data) ? data : (data.results || [])).forEach((c) => {
          const city = normalizeCityForHierarchy(c.city_name || "");
          if (!city) return;
          if (!map[city]) map[city] = [];
          map[city].push(c);
        });
        setClustersByCity(map);
      })
      .catch(() => {});
  };

  useEffect(() => { loadClusters(); }, [refreshKey]);

  const selectedUser = users.find((u) => String(u.id) === selectedUserId) || null;

  useEffect(() => {
    if (!selectedUser) { setCityNames([]); setClusterScopesByCity({}); return; }
    const assigned = (selectedUser.geography_scopes || [])
      .map((s) => normalizeCityForHierarchy(s.city_name || ""))
      .filter(Boolean);
    setCityNames(assigned);
    const scopeMap = {};
    (selectedUser.geography_scopes || []).forEach((s) => {
      const city = normalizeCityForHierarchy(s.city_name || "");
      if (city) scopeMap[city] = new Set(s.cluster_scopes || []);
    });
    setClusterScopesByCity(scopeMap);
  }, [selectedUserId, users]);

  const toggle = (city) => {
    setCityNames((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };

  const toggleCluster = (city, clusterName) => {
    setClusterScopesByCity((prev) => {
      const next = { ...prev };
      const set = new Set(next[city] || []);
      if (set.has(clusterName)) set.delete(clusterName); else set.add(clusterName);
      next[city] = set;
      return next;
    });
  };

  const selectAllClusters = (city) => {
    setClusterScopesByCity((prev) => ({ ...prev, [city]: new Set() }));
  };

  const handleSave = async () => {
    if (!selectedUserId) return;
    const clusterPayload = {};
    cityNames.forEach((city) => {
      const set = clusterScopesByCity[city];
      clusterPayload[city] = set && set.size > 0 ? Array.from(set) : [];
    });
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await fetchAdminJson(`/api/accounts/users/${selectedUserId}/`, {
        method: "PATCH",
        body: JSON.stringify({ city_names: cityNames, cluster_scopes: clusterPayload }),
      });
      setNotice("Saved!");
      setTimeout(() => setNotice(""), 2500);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const regionGroups = Object.keys(HIERARCHY_CITY_REGION).reduce((acc, city) => {
    const r = HIERARCHY_CITY_REGION[city];
    if (!acc[r]) acc[r] = [];
    acc[r].push(city);
    return acc;
  }, {});

  return (
    <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "260px 1fr" }}>
      {/* User list */}
      <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
        <div style={{ background: "var(--surface-sunken)", borderBottom: "1px solid var(--border)", color: "var(--text-tertiary)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.07em", padding: "0.6rem 1rem", textTransform: "uppercase" }}>
          {roleLabel}
        </div>
        <div style={{ maxHeight: "480px", overflowY: "auto" }}>
          {users.length === 0 ? (
            <div style={{ color: "var(--text-tertiary)", fontSize: "13px", padding: "1rem" }}>No users found.</div>
          ) : users.map((u) => {
            const active = String(u.id) === selectedUserId;
            const assigned = (u.geography_scopes || [])
              .map((s) => normalizeCityForHierarchy(s.city_name || ""))
              .filter(Boolean);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => setSelectedUserId(String(u.id))}
                style={{
                  background: active ? "var(--primary-alpha, #eef2ff)" : "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--border)",
                  borderLeft: active ? "3px solid var(--primary)" : "3px solid transparent",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                  display: "block",
                  padding: "0.65rem 0.875rem",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <div style={{ color: active ? "var(--primary)" : "var(--text-primary)", fontSize: "13px", fontWeight: active ? 600 : 400 }}>{u.full_name}</div>
                <div style={{ color: "var(--text-tertiary)", fontSize: "11px", marginTop: "2px" }}>
                  {assigned.length > 0 ? assigned.join(", ") : <em>No cities assigned</em>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* City + cluster picker */}
      {!selectedUser ? (
        <div style={{ alignItems: "center", border: "2px dashed var(--border)", borderRadius: "10px", color: "var(--text-tertiary)", display: "flex", fontSize: "14px", justifyContent: "center", minHeight: "240px" }}>
          Select a {roleLabel.slice(0, -1).toLowerCase()} to assign cities
        </div>
      ) : (
        <div style={{ display: "grid", alignContent: "start", gap: "1.25rem" }}>
          {error ? <div className="message-banner error">{error}</div> : null}
          {notice ? <div className="message-banner success">{notice}</div> : null}

          {/* Header */}
          <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700 }}>{selectedUser.full_name}</div>
              <div style={{ color: "var(--text-tertiary)", fontSize: "12px", marginTop: "2px" }}>
                {cityNames.length === 0 ? "No cities assigned" : `${cityNames.length} ${cityNames.length === 1 ? "city" : "cities"} assigned`}
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="button" className="secondary-ghost" style={{ fontSize: "12px", padding: "4px 12px" }} onClick={() => setCityNames(Object.keys(HIERARCHY_CITY_REGION))}>Select All</button>
              <button type="button" className="secondary-ghost" style={{ fontSize: "12px", padding: "4px 12px" }} onClick={() => setCityNames([])}>Clear</button>
            </div>
          </div>

          {/* Region groups */}
          <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
            {Object.entries(regionGroups).map(([region, cities], i) => (
              <div key={region} style={{ borderBottom: i < Object.entries(regionGroups).length - 1 ? "1px solid var(--border)" : "none", padding: "1rem 1.25rem" }}>
                <div style={{ alignItems: "center", display: "flex", gap: "0.75rem", marginBottom: "0.6rem" }}>
                  <span style={{ background: "var(--surface-sunken)", borderRadius: "4px", color: "var(--text-secondary)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", padding: "2px 7px", textTransform: "uppercase" }}>{region}</span>
                  <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>
                    {cities.filter(c => cityNames.includes(c)).length}/{cities.length} selected
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {cities.map((city) => {
                    const on = cityNames.includes(city);
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={() => toggle(city)}
                        style={{
                          background: on ? "#3b5bdb" : "var(--surface-sunken)",
                          border: `1.5px solid ${on ? "#3b5bdb" : "var(--border)"}`,
                          borderRadius: "6px",
                          color: on ? "#ffffff" : "var(--text-primary)",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: on ? 600 : 400,
                          padding: "0.35rem 0.9rem",
                          transition: "background 0.12s, border-color 0.12s, color 0.12s",
                        }}
                      >
                        {on ? "✓ " : ""}{city}
                      </button>
                    );
                  })}
                </div>

                {/* Cluster sub-assignment for selected cities */}
                {cities.filter(c => cityNames.includes(c)).map((city) => {
                  const clusters = clustersByCity[city];
                  if (!clusters || clusters.length === 0) return null;
                  const selectedClusters = clusterScopesByCity[city] || new Set();
                  const allClusters = selectedClusters.size === 0;
                  return (
                    <div key={city} style={{ marginTop: "0.75rem", background: "var(--surface-sunken)", borderRadius: "8px", padding: "0.6rem 0.75rem", border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
                        <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 600 }}>{city} clusters</span>
                        <button
                          type="button"
                          onClick={() => selectAllClusters(city)}
                          style={{
                            background: allClusters ? "#3b5bdb" : "transparent",
                            border: `1px solid ${allClusters ? "#3b5bdb" : "var(--border)"}`,
                            borderRadius: "4px",
                            color: allClusters ? "#fff" : "var(--text-tertiary)",
                            cursor: "pointer",
                            fontSize: "11px",
                            padding: "1px 8px",
                          }}
                        >
                          All clusters
                        </button>
                        {!allClusters && (
                          <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>
                            {selectedClusters.size} of {clusters.length} selected
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                        {clusters.map((cl) => {
                          const checked = !allClusters && selectedClusters.has(cl.name);
                          return (
                            <button
                              key={cl.id}
                              type="button"
                              onClick={() => toggleCluster(city, cl.name)}
                              title={`${cl.pincode_count} pincodes`}
                              style={{
                                background: checked ? "#e7f5ff" : "var(--surface)",
                                border: `1.5px solid ${checked ? "#74c0fc" : "var(--border)"}`,
                                borderRadius: "6px",
                                color: checked ? "#1971c2" : "var(--text-primary)",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: checked ? 600 : 400,
                                padding: "4px 10px",
                              }}
                            >
                              {checked ? "✓ " : ""}{cl.name}
                              <span style={{ color: checked ? "#74c0fc" : "var(--text-tertiary)", fontSize: "10px", marginLeft: 4 }}>
                                {cl.pincode_count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {/* Pincodes of selected clusters */}
                      {!allClusters && clusters.filter(cl => selectedClusters.has(cl.name)).some(cl => cl.pincodes?.length > 0) && (
                        <div style={{ marginTop: "0.5rem", borderTop: "1px solid var(--border)", paddingTop: "0.5rem" }}>
                          {clusters.filter(cl => selectedClusters.has(cl.name)).map(cl => (
                            cl.pincodes?.length > 0 ? (
                              <div key={cl.id} style={{ marginBottom: "0.35rem" }}>
                                <span style={{ fontSize: "11px", fontWeight: 600, color: "#1971c2", marginRight: "0.4rem" }}>{cl.name}:</span>
                                {cl.pincodes.map((p) => (
                                  <span key={p.id} style={{
                                    display: "inline-block",
                                    fontSize: "11px",
                                    background: "#e7f5ff",
                                    border: "1px solid #74c0fc",
                                    borderRadius: "4px",
                                    padding: "1px 6px",
                                    marginRight: "4px",
                                    marginBottom: "3px",
                                    color: "#1971c2",
                                    fontFamily: "monospace",
                                  }}>
                                    {p.pincode}{p.area_name ? ` · ${p.area_name}` : ""}
                                  </span>
                                ))}
                              </div>
                            ) : null
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" className="primary-button" disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save Assignment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AssignmentsTab ─────────────────────────────────────────────────────────────

export function AssignmentsTab({ onContextChange }) {
  const [activeAssignTab, setActiveAssignTab] = useState("manager");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [agentAssignmentFile, setAgentAssignmentFile] = useState(null);
  const [agentAssignmentUploading, setAgentAssignmentUploading] = useState(false);
  const [agentAssignmentResult, setAgentAssignmentResult] = useState(null);
  const [assignmentRefreshKey, setAssignmentRefreshKey] = useState(0);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const allUsers = await fetchAllPages("/api/accounts/users/");
      setUsers(allUsers.filter((u) => u.role !== "admin"));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const managers = users.filter((u) => u.role === "manager" || u.role === "senior_manager");
  const agents = users.filter((u) => u.role === "agent" || u.role === "btl");

  const handleAgentAssignmentUpload = async () => {
    if (!agentAssignmentFile) return;
    try {
      setAgentAssignmentUploading(true);
      setError("");
      setAgentAssignmentResult(null);
      const formData = new FormData();
      formData.append("file", agentAssignmentFile);
      const result = await fetchAdminJson("/api/crm/clusters/assign-import/", {
        method: "POST",
        body: formData,
      });
      setAgentAssignmentResult(result);
      setAgentAssignmentFile(null);
      await loadData();
      setAssignmentRefreshKey((current) => current + 1);
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setAgentAssignmentUploading(false);
    }
  };

  if (loading) return <section className="report-state-card">Loading...</section>;

  return (
    <div style={{ display: "grid", gap: "1.25rem" }}>
      {error ? <div className="message-banner error">{error}</div> : null}

      <div style={{ alignItems: "center", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", gap: 12, marginBottom: "0.25rem" }}>
        <div className="report-tab-strip" style={{ borderBottom: 0, marginBottom: 0 }}>
          {[
            { key: "manager", label: "Manager Assignment" },
            { key: "agent", label: "Agent Assignment" },
            { key: "clusters", label: "Clusters" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`report-tab-button ${activeAssignTab === key ? "active" : ""}`}
              onClick={() => setActiveAssignTab(key)}
            >
              <span>{label}</span>
            </button>
          ))}
        </div>

        {activeAssignTab === "agent" ? (
          <div style={{ alignItems: "center", display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <label className="secondary-ghost" style={{ cursor: "pointer", fontSize: 12, padding: "6px 12px" }}>
              {agentAssignmentFile ? agentAssignmentFile.name : "Choose CSV/XLSX"}
              <input
                type="file"
                accept=".csv,.xlsx"
                style={{ display: "none" }}
                onChange={(event) => {
                  setAgentAssignmentFile(event.target.files?.[0] || null);
                  setAgentAssignmentResult(null);
                }}
              />
            </label>
            <button
              type="button"
              className="primary-button"
              disabled={!agentAssignmentFile || agentAssignmentUploading}
              onClick={handleAgentAssignmentUpload}
              style={{ fontSize: 12, minHeight: 34, padding: "0 14px" }}
            >
              {agentAssignmentUploading ? "Uploading..." : "Upload Assignment"}
            </button>
            <button
              type="button"
              className="secondary-ghost"
              onClick={downloadAgentAssignmentSample}
              style={{ fontSize: 12, padding: "6px 12px" }}
            >
              Sample File
            </button>
            <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>
              Columns: Cluster · City · SPOC
            </span>
          </div>
        ) : null}
      </div>

      {activeAssignTab === "agent" && agentAssignmentResult ? (
        <div className={agentAssignmentResult.errors?.length ? "message-banner warning" : "message-banner success"}>
          {agentAssignmentResult.assigned || 0} assignment row{agentAssignmentResult.assigned === 1 ? "" : "s"} processed.
          {" "}
          {agentAssignmentResult.created || 0} pincode row{agentAssignmentResult.created === 1 ? "" : "s"} created, {agentAssignmentResult.updated || 0} updated.
          {agentAssignmentResult.errors?.length ? ` ${agentAssignmentResult.errors.length} row${agentAssignmentResult.errors.length === 1 ? "" : "s"} failed.` : ""}
        </div>
      ) : null}

      {activeAssignTab === "agent" && agentAssignmentResult?.errors?.length ? (
        <div className="message-banner error" style={{ display: "grid", gap: 6 }}>
          {agentAssignmentResult.errors.slice(0, 5).map((item, index) => (
            <span key={index}>{item.error}</span>
          ))}
          {agentAssignmentResult.errors.length > 5 ? <span>...and {agentAssignmentResult.errors.length - 5} more.</span> : null}
        </div>
      ) : null}

      {activeAssignTab === "manager" ? (
        <CityAssignmentPanel users={managers} roleLabel="Managers" onSaved={() => { loadData(); onContextChange?.(); }} />
      ) : activeAssignTab === "agent" ? (
        <CityAssignmentPanel
          users={agents}
          roleLabel="Agents"
          refreshKey={assignmentRefreshKey}
          onSaved={() => { loadData(); onContextChange?.(); }}
        />
      ) : (
        <ClustersTab />
      )}
    </div>
  );
}

export default AssignmentsTab;
